import React, { useCallback, useEffect, useRef, useState } from 'react';
import CandleHop from './CandleHop';
import { roomVerbHint } from '../../utils/roomVerbs';

/**
 * 🚪📅 ComposerDoors (09-20, Nina picked mockup option B
 * https://claude.ai/artifact/8qs3auiYAMuxJj8s2KG1jj): the rooms door and the
 * calendar door stop being two tall framed boxes LEFT of the message box and
 * become two quiet text buttons.
 *   · desktop: on the bottom edge INSIDE the box (HumanInputForm's .hif-box)
 *   · phones:  in the tool strip under the box (the box is one line tall
 *              there, and the old doors cost 108px of typing width)
 *
 * Why the old pair read clunky: they stretched to the full height of the
 * composer (a 17px glyph in a ~90px box), and the 📅 emoji is a page that
 * says JUL 17 on a Mac, so "17 / Tue" read as "Tuesday the 17th". The calendar
 * door now DRAWS its leaf with the real day of the next featured night.
 *
 * These REPLACE RoomPicker.RoomDoorButton + CalendarDoor.CalendarDoorButton in
 * the composer (both still exported, with their tests). The contract those
 * carried is kept verbatim, because GameStepPage's outside-click exemptions
 * and the a11y names key on it: data-room-door / data-calendar-door, the
 * aria-labels, aria-haspopup + aria-expanded, and the swallowed mousedown so
 * a tap never steals focus from the message box.
 * ⚠️ This frontend has NO global box-sizing: every padded box sets it.
 */
const GOLD = '#e9b565';
const YOU = '#ffd98a';
const DUST = '#b3a187';
const SOON_GREEN = '#22c55e'; // = the Arcade tab's dot (GameStepPage): an invitation, not an alert

const WEEKDAY3 = /^(mon|tue|wed|thu|fri|sat|sun)$/i;

/** "the hall bathroom" → "Hall bathroom" (the door says where you are). */
export function roomDoorWords(label) {
  const s = String(label || 'the main room').trim().replace(/^the\s+/i, '');
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Main room';
}

/**
 * The calendar door's words, from calendarView.doorLabel's `text` plus the
 * next featured night's `day` ('YYYY-MM-DD').  num = the TRUE day of month
 * (never the emoji's 17); words = "Tue 22" · "Tomorrow" · "Today" · "On now"
 * · "Oct 31". No day → no leaf number, words fall back to the text.
 */
export function calendarDoorWords(text, day) {
  const t = String(text || '').trim();
  const n = /^\d{4}-\d{2}-\d{2}$/.test(String(day || '')) ? Number(String(day).slice(8, 10)) : null;
  const num = Number.isFinite(n) && n >= 1 && n <= 31 ? String(n) : '';
  let words = t;
  if (t === 'now') words = 'On now';
  else if (t === 'soon') words = 'Starting soon'; // ⏰ 10-08: a passed start the engine has not begun (calendarView.leafClock)
  else if (t === 'today') words = 'Today';
  else if (t === 'tmrw') words = 'Tomorrow';
  else if (WEEKDAY3.test(t) && num) words = `${t} ${num}`;
  return { num, words };
}

const base = (lit, hot, compact) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, boxSizing: 'border-box',
  flex: '0 1 auto', minWidth: 0, maxWidth: '100%',
  height: compact ? 40 : 24, padding: compact ? '0 9px' : '0 8px',
  borderRadius: 999, cursor: 'pointer', font: 'inherit',
  fontSize: compact ? 13 : 12.5, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap',
  background: lit ? 'rgba(233,181,101,0.16)' : 'transparent',
  border: `1px solid ${lit ? GOLD : 'transparent'}`,
  color: hot ? YOU : (lit ? GOLD : DUST),
});

const words = { overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };

// 🐢 `walking` = { from, to, label } while a slow room change waits on the house
// (RoomTransit stepped aside, desktop): the candle hops from room to room
// inside the door instead of the room's name (D, Nina 09-24: "for when we're
// waiting for something else"). null = the door as always.
export function InlineRoomDoor({ icon, label, open = false, onToggle, compact = false, walking = null }) {
  return (
    <button type="button" className="hif-door" data-room-door="1"
      aria-label={walking ? `Rooms. On your way to ${walking.label || 'another room'}` : `Rooms. You are in ${label || 'the main room'}`} aria-haspopup="dialog" aria-expanded={open}
      title="Rooms"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { if (onToggle) onToggle(); }}
      style={{ ...base(open, false, compact), ...(compact ? null : { flexShrink: 10 }) /* 🧭 10-01 composer: on a crowded desktop floor the room's NAME ellipsizes before the clock */ }}>
      {walking ? (
        <span aria-hidden="true" data-testid="room-door-walking" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          <span style={{ fontSize: compact ? 17 : 14 }}>{walking.from || '🕯️'}</span>
          <CandleHop />
          <span style={{ fontSize: compact ? 17 : 14 }}>{walking.to || '🚪'}</span>
        </span>
      ) : (
        <>
          <span aria-hidden="true" style={{ fontSize: compact ? 17 : 14, flexShrink: 0 }}>{icon || '🕯️'}</span>
          {/* phones: icon + ▾ only, the plate above already names the room */}
          {!compact && <span aria-hidden="true" style={words}>{roomDoorWords(label)}</span>}
        </>
      )}
      <span aria-hidden="true" style={{ fontSize: 9, flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
    </button>
  );
}

// 🕐 09-24 (Nina: option C, revised): `face` = calendarView.doorFace(): the leaf
// is TODAY's house date, the words are the house clock + a weather word at rest,
// the countdown on an event's day, "On now" while it runs; `soon` dots the leaf
// for a featured night tomorrow. Without `face` the door reads text/day as before.
// 🌿 `leafOnly` (09-25, Nina: option B): in a room the box's floor also carries
// the room's verbs, so the door folds to its leaf at rest. An event ("On now",
// a countdown) still shows its words: that is news, the clock is not.
// 🧭 10-01 composer (R12, Nina: "the calendar can have some room and have the
// clock back"): the composer no longer passes `leafOnly`, so the clock + weather
// word are back in every room on both screens. Kept as the one-line revert.
// Under 340px wide the weather word hides (HumanInputForm's injected style,
// by width only), so the doors and ＋ ➤ keep one line on the smallest phones.
export function InlineCalendarDoor({ text = '', day = null, hot = false, title = 'House calendar', open = false, onToggle, compact = false, face = null, leafOnly = false }) {
  const lit = open || hot;
  const w0 = face ? { num: face.num || '', words: face.words || '' } : calendarDoorWords(text, day);
  const w = (leafOnly && !hot) ? { ...w0, words: '' } : w0;
  const soon = !!(face && face.soon && !hot);
  const weather = (face && !hot && !leafOnly && face.weather) || null;
  return (
    <button type="button" className="hif-door" data-calendar-door="1"
      aria-label={title} aria-haspopup="dialog" aria-expanded={open} title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { if (onToggle) onToggle(); }}
      style={{ ...base(lit, hot, compact), ...(compact ? null : { flexShrink: 0 }) /* 🧭 10-01 composer: the clock is never the thing that clips (the band, then the room name, yield first) */ }}>
      {/* the drawn leaf: a header bar + the real day number, in currentColor */}
      <span aria-hidden="true" data-calendar-leaf={w.num || 'blank'} data-calendar-soon={soon ? '1' : undefined} style={{
        position: 'relative', flexShrink: 0, boxSizing: 'border-box',
        width: compact ? 19 : 17, height: compact ? 19 : 17, paddingTop: 3,
        border: '1.5px solid currentColor', borderRadius: 4,
        display: 'grid', placeItems: 'center',
        fontSize: compact ? 9.5 : 8.5, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
      }}>
        <span style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 4, background: 'currentColor', opacity: 0.85 }} />
        {w.num}
        {/* a featured night tomorrow: a GREEN dot (Nina 09-25: gold hid on the
            gold door), the Arcade tab's invitation green, whatever color the door is */}
        {soon && <span data-testid="calendar-soon" style={{ position: 'absolute', right: -4, top: -4, width: 8, height: 8, borderRadius: '50%', background: SOON_GREEN, boxShadow: '0 0 0 1.5px rgba(24,19,14,0.96), 0 0 6px rgba(34,197,94,0.6)' }} />}
      </span>
      {w.words && <span aria-hidden="true" style={words}>{w.words}</span>}
      {weather && (
        <span aria-hidden="true" data-calendar-weather={weather} data-testid="calendar-weather" style={{ flexShrink: 0 }}>
          <span style={{ opacity: 0.55, padding: '0 4px 0 1px' }}>·</span>{weather}
        </span>
      )}
    </button>
  );
}

/**
 * 🌿 The room's floor chips (09-25, Nina: option B + D, mockup
 * https://claude.ai/artifact/SKDBxsmPaRKqDGoYyCZPrW). The room bar that sat
 * ABOVE the box is gone: in any room but the main one, the things you can do
 * there ride the box's floor after the doors (desktop) or the tool strip
 * (phones), and who is here moves into the empty box (HumanInputForm).
 *
 * A chip is the same string the bar used: a command ("/water",
 * "/tell-the-tomatoes ") or an action ("▶ Watch with the room"). Desktop shows
 * emoji + command; phones show the EMOJI ONLY for commands (Nina: save room),
 * and an action keeps one word, because a lone ▶ or 🔗 says nothing. Every
 * chip names itself for hover and screen readers; on touch the tap seeds the
 * box and the arg bar shows the same sentence.
 * 🧭 10-01 composer (R12, Nina: "get rid of the emojis from the rooms'
 * /commands buttons in the desktop textbox"): desktop commands are the WORDS
 * only now ("/water", "/look"); phones keep the emoji-only commands. Actions
 * keep their glyph (▶ Watch with the room, 📚 Shelf), except Share, which is
 * the plain word on every screen ("the share button still has the paperclip
 * emoji (please remove)"). data-floor-chip keeps the RAW string ("🔗 Share"):
 * the page's onVerb, the jest pins and the browser gates key on it.
 */
// 🧭 10-01 composer: actions that read as the bare word (no glyph), on every screen
const BARE_ACTION = /^share$/i;
export function floorChip(v, emojiFor) {
  const s = String(v || '').trim();
  if (s[0] === '/') {
    const word = s.slice(1).split(/\s/)[0].toLowerCase();
    const emoji = (typeof emojiFor === 'function' && emojiFor(word)) || '✦';
    const hint = roomVerbHint(s);
    return { command: true, emoji, words: s, short: '', title: hint ? `${s}: ${hint}` : s };
  }
  const m = /^(\S+)\s+(.+)$/.exec(s);
  if (!m) return { command: false, emoji: '', words: s, short: s, title: s };
  // 🧭 10-01 composer (R12): "🔗 Share" draws as "Share" (and names itself "Share")
  if (BARE_ACTION.test(m[2].trim())) return { command: false, emoji: '', words: m[2].trim(), short: m[2].trim(), title: m[2].trim() };
  // 📚 "📖 The Long Title · p. 4": a phone keeps what comes before " · " (a title), not its first word
  const short = m[2].includes(' · ') ? m[2].split(' · ')[0] : m[2].split(/\s/)[0];
  return { command: false, emoji: m[1], words: m[2], short, title: s };
}

const FLOOR_EDGE = 'linear-gradient(90deg, #000 calc(100% - 28px), transparent)';
// 🧭 10-01 composer: the soft edge sits on the side with MORE (left once you have scrolled, right while more waits)
const FLOOR_EDGE_L = 'linear-gradient(90deg, transparent, #000 28px)';
const FLOOR_EDGE_LR = 'linear-gradient(90deg, transparent, #000 28px, #000 calc(100% - 28px), transparent)';
const FLOOR_FADE_PX = 28; // 🧭 10-01 composer (review): the soft edge's width (the 28px in the three masks above)

// 🧭 10-01 composer (R12, Nina: "move them and the emoji buttons on mobile all
// so they're right aligned"): the chips ride ONE inner run with an auto left
// margin, so they sit at the RIGHT end of the lane. ⚠️ never justify-content:
// flex-end on the lane: it is the scroll box, and flex-end makes the first
// chips unreachable when the run overflows. An auto margin collapses to 0
// instead, so an overflowing run opens at its FIRST chip and scrolls right.
// The band (🧺 basket, 🏮 maze, the screening) is status, not a button:
//   · desktop: it leaves the lane and sits LEFT of it, and it yields first
//     (it ellipsizes before the calendar clock clips; the lane keeps at least
//     its first chip's width)
//   · phones ("One line, buttons scroll"): it rides AFTER the chips, so /pick
//     shows without a swipe
export function RoomVerbLane({ chips = [], band = null, onVerb, onLook, emojiFor, compact = false, lit = null }) {
  const ref = useRef(null);
  const [edge, setEdge] = useState(false);
  const [edgeL, setEdgeL] = useState(false); // 🧭 10-01 composer: scrolled = a soft LEFT edge too
  const [keep, setKeep] = useState(0); // 🧭 10-01 composer: desktop lane floor = its first chip's width (+ the fade when more follow, review)
  const bandText = (band && band.text) || '';
  const chipKey = chips.join('|');
  // the soft right edge only while there is more lane to scroll to
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdge(el.scrollWidth - el.clientWidth - el.scrollLeft > 2);
    setEdgeL(el.scrollLeft > 2); // 🧭 10-01 composer
    // 🧭 10-01 composer: desktop only (on phones the lane's wrapper already owns the leftover width)
    // 🧭 10-01 composer (review): + the soft edge's width when more chips follow, so the right fade
    // sits over the gap and the NEXT chip, never over the first one (measured at 820px: the lane was
    // exactly /pick wide and the fade ate half of it; the maze's /left at 1280 the same)
    if (!compact) {
      const first = el.querySelector('[data-floor-chip]');
      const more = !!(first && first.nextElementSibling);
      setKeep(first ? Math.ceil(first.offsetWidth) + (more ? FLOOR_FADE_PX : 0) : 0);
    }
  }, [compact]);
  useEffect(() => {
    measure();
    const el = ref.current;
    const ro = (el && typeof ResizeObserver !== 'undefined') ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => { if (ro) ro.disconnect(); };
  }, [measure, chips.length, bandText, chipKey]);
  const chipStyle = (icon, on = false) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, flexShrink: 0, boxSizing: 'border-box',
    height: compact ? 34 : 22, minWidth: compact ? 34 : 0, padding: compact ? (icon ? 0 : '0 9px') : '0 8px',
    borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1,
    border: '1px solid rgba(122,96,60,0.55)', background: 'rgba(47,35,24,0.9)', color: '#e6dac6',
    fontFamily: 'inherit', fontSize: compact ? (icon ? 17 : 13) : 11.5,
    // 📚 a lit chip (the library's 📚 Shelf / 📖 book): lamp gold, the mockup's .vb.shelf
    ...(on ? { border: '1px solid #a8773a', background: 'rgba(54,40,24,0.95)', color: '#f2b35e', fontWeight: 700 } : {}),
  });
  const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11, maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis' };
  const list = chips.map((v) => ({ v, c: floorChip(v, emojiFor) }));
  const mask = edge && edgeL ? FLOOR_EDGE_LR : (edge ? FLOOR_EDGE : (edgeL ? FLOOR_EDGE_L : null)); // 🧭 10-01 composer
  // 🧭 10-01 composer: one band element, placed by screen (desktop: left of the lane · phones: after the chips)
  const bandEl = (band && band.text) ? (
    <span data-floor-band="1" style={compact ? {
      flexShrink: 0, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      fontSize: 12.5, fontWeight: 800, padding: '0 4px', color: band.hot ? '#ffd98a' : '#e9b565',
    } : {
      // desktop: it yields FIRST (a huge shrink weight), down to its glyph, before a door clips
      flex: '0 1000 auto', minWidth: 28, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      fontSize: 11.5, fontWeight: 800, padding: '0 4px', boxSizing: 'border-box', color: band.hot ? '#ffd98a' : '#e9b565',
    }}>{band.text}</span>
  ) : null;
  return (
    <>
      {!compact && bandEl}
      <div ref={ref} data-room-floor="1" onScroll={measure}
        onWheel={(e) => {
          // a mouse wheel scrolls the lane sideways (a trackpad already does)
          const el = ref.current;
          if (el && Math.abs(e.deltaY) > Math.abs(e.deltaX) && el.scrollWidth > el.clientWidth) el.scrollLeft += e.deltaY;
        }}
        style={{
          display: 'flex', alignItems: 'center', minWidth: compact ? 0 : keep, flex: '1 1 0',
          overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none',
          ...(mask ? { WebkitMaskImage: mask, maskImage: mask } : {}),
        }}>
        {/* 🧭 10-01 composer: the run, pushed right by its auto margin (0 when it overflows) */}
        <div data-floor-run="1" style={{ display: 'flex', alignItems: 'center', gap: compact ? 4 : 5, marginLeft: 'auto', flexShrink: 0 }}>
          {list.map(({ v, c }) => {
            const icon = compact && c.command;
            const on = Array.isArray(lit) && lit.includes(v);
            return (
              <button key={v} type="button" data-floor-chip={String(v).trim()} data-floor-lit={on ? '1' : undefined}
                title={c.title} aria-label={c.title}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { if (onVerb) onVerb(v); }}
                style={chipStyle(icon, on)}>
                {/* 🧭 10-01 composer: desktop commands are words only; phones keep the emoji; actions keep their glyph */}
                {c.emoji && (compact || !c.command) && <span aria-hidden="true">{c.emoji}</span>}
                {icon ? null : (compact
                  ? <span aria-hidden="true" style={{ fontWeight: 700 }}>{c.short}</span>
                  : <span aria-hidden="true" style={c.command ? mono : { fontWeight: 700 }}>{c.words}</span>)}
              </button>
            );
          })}
          {onLook && (
            <button type="button" data-floor-chip="/look" title="Look around (only you see this)" aria-label="Look around (only you see this)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onLook}
              style={chipStyle(compact)}>
              {/* 🧭 10-01 composer: 👀 on phones only, desktop reads "/look" */}
              {compact && <span aria-hidden="true">👀</span>}
              {!compact && <span aria-hidden="true" style={mono}>/look</span>}
            </button>
          )}
          {compact && bandEl}
        </div>
      </div>
    </>
  );
}
